import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import ShoppingCart from "./pages/ShoppingCart";
import Success from "./pages/SuccessPage";
import Cancel from "./pages/CancelPage";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ProfileComplete from "./pages/ProfileComplete";
import Hub from "./pages/Hub";
import Profile from "./pages/Epk";
import EpkUpdate from "./pages/EpkUpdate";
import AudioMix from "./components/AudioMix";
import Voicemail from "./components/Voicemail";
import AddEpisode from './components/AddEpisode';
import AddPost from './components/AddPost';
import Calendar from './components/Calendar';
import Registry from './components/Registry';
import Registrations from './components/HubRegistrations';
import Confirmation from './components/SignupThankYou';
import Pending from './components/SignupPending';
import NewsletterArchive from './pages/NewsletterArchive';
import NewsletterView from './pages/Newsletter';
import NewsletterEditor from './pages/NewsletterEditor';
import AddAlbum from './components/AddAlbum';
import AudioRecorder from './components/AudioRecorder';
import KingOfGlory from './pages/KingOfGloryRelease';
import PrivacyPage from './pages/PrivacyPage';
import CatalogPage from './pages/CatalogPage';
import DonatePage from './pages/DonatePage';
import FooterPlayer from './components/FooterPlayer';
import AboutPage from "./pages/AboutPage";
import EventsPage from "./pages/EventsPage";
import MusicPage from "./pages/MusicPage";
import ProgramsPage from "./pages/ProgramsPage";
import SoulSessions from './pages/SoulSessions';
import Storytime from './pages/Storytime';
import RadioPage from './pages/RadioPage';
import ShopPage from "./pages/ShopPage";
import Dashboard from './pages/Dashboard';
import Partnerships from './pages/Partnerships';
import TaskBoard from './pages/TaskBoard';
import Contacts from './pages/Contacts';
import GrantTracker from './pages/GrantTracker';
import Projects from './pages/Projects';
import SamplingPage from './pages/SamplingPage';
import SamplingAuthoring from './pages/SamplingAuthoring';
import BlogPost from './pages/BlogPost';
import NotePost from './pages/NotePost';
import DrumPad from './pages/DrumPad';
import DrumTalk from './pages/DrumTalk';
import Alphariddims from './components/AlphariddimsSequencerButtons';
import RhythmExplorer from './components/EuclideanSequencer_Buttons';


// Uses react-router-dom's <Routes> (already available — index.js wraps the
// app in <BrowserRouter>, it just wasn't being used here). This fixes a
// real bug in the old manual pathname-matching version: the old
// path.startsWith("/newsletter") check ran before the exact "/newsletters"
// check, and since "/newsletters" also starts with "/newsletter", the
// NewsletterArchive branch could never actually be reached. Route matching
// here is exact per path, so that collision goes away on its own.
//
// Dynamic segments (:token, :id) aren't consumed here — the target pages
// (EpkUpdate, Profile, NewsletterEditor) were already reading the token/id
// straight from window.location.pathname rather than via props, so they
// keep working unchanged. Swap them to useParams() in those files later if
// you want the cleaner version of that too.
//
// The catch-all route intentionally preserves the old behavior of falling
// back to LandingPage for any unmatched path (rather than a 404) — same
// behavior as before, just explicit now.

/*

Replace FooterPlayer such that it appears in pages as needed by route, not every page across the entire site

*/

// Only these routes show the footer player. /music, /radio, and /featured
// are unambiguous (releases page, the radio show, and a specific single's
// release page). Left out as judgment calls, not certainties — add either
// if you want the player there too:
//   /shop     — sells vinyl/merch alongside music, but is fundamentally a shop
//   /soulsessions — a live poetry+music night, arguably music-adjacent
const MUSIC_PAGE_PATHS = ["/", "/music", "/radio", "/featured", "/events"];

function App() {
  const location = useLocation();
  const showFooterPlayer = MUSIC_PAGE_PATHS.includes(location.pathname);
  const footerPlayerSource = location.pathname === "/events" ? "featured" : "radio";

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/soulsessions" element={<SoulSessions />} />
        <Route path="/storytime" element={<Storytime />} />
        <Route path="/drumtalk" element={<DrumTalk />} />
        <Route path="/drumpad" element={<DrumPad />} />
        <Route path="/alphariddims" element={<Alphariddims />} />
        <Route path="/rhythmexplorer" element={<RhythmExplorer />} />
        <Route path="/radio" element={<RadioPage />} />
        <Route path="/music" element={<MusicPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/shop" element={<ShopPage />} />

        {/* Shopping cart and checkout */}
        <Route path="/shopping-cart" element={<ShoppingCart />} />
        <Route path="/cart" element={<ShoppingCart />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />

        {/* Hub (protected) */}
        <Route path="/hub" element={<Hub />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/grants" element={<GrantTracker />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/sampling" element={<SamplingPage />} />
        <Route path="/sampling/authoring" element={<SamplingAuthoring />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route path="/notes/:id" element={<NotePost />} />
        
        {/* Internal Dashboard */}


        {/* Auth */}
        <Route path="/register" element={<Register />} />
        <Route path="/signup" element={<Register />} />
        <Route path="/signin" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/profile-complete" element={<ProfileComplete />} />

        {/* Newsletter — admin editor */}
        <Route path="/admin/newsletters/new" element={<NewsletterEditor />} />
        <Route path="/admin/newsletters/:id" element={<NewsletterEditor />} />

        {/* Newsletter — public archive + view (order no longer matters,
            unlike the old startsWith chain) */}
        <Route path="/newsletters" element={<NewsletterArchive />} />
        <Route path="/newsletter" element={<NewsletterView />} />
        <Route path="/newsletter/:id" element={<NewsletterView />} />

        {/* EPK update (tokenized links) */}
        <Route path="/epk-update/:token" element={<EpkUpdate />} />
        <Route path="/artist-profile-complete/:token" element={<EpkUpdate />} />

        {/* EPK / profile */}
        <Route path="/epk/:id" element={<Profile />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/epk" element={<Profile />} />

        {/* Hub tools */}
        <Route path="/audio" element={<AudioMix />} />
        <Route path="/record" element={<AudioRecorder />} />
        <Route path="/voicemail" element={<Voicemail />} />
        <Route path="/registry" element={<Registry />} />
        <Route path="/newEpisode" element={<AddEpisode />} />
        <Route path="/post" element={<AddPost />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/registrations" element={<Registrations />} />
        <Route path="/confirmation" element={<Confirmation />} />
        <Route path="/pending" element={<Pending />} />
        <Route path="/newAlbum" element={<AddAlbum />} />

        {/* One-off feature pages */}
        <Route path="/featured" element={<KingOfGlory />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/donate" element={<DonatePage />} />

        {/* Fallback — same behavior as the old default branch */}
        <Route path="*" element={<LandingPage />} />
      </Routes>

      {showFooterPlayer && <FooterPlayer source={footerPlayerSource} />}
    </div>
  );
}

export default App;